import { FC, Fragment } from 'react';
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import type { ButtonProps, MenuProps } from 'antd';
import { Badge, Button, Col, Dropdown, Menu, Row, Tag } from 'antd';
import { TbArrowsSort, TbBackspace, TbClock, TbEdit, TbForms } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useQuery } from '@/hooks/useQuery';
import { EmptySection } from '@/shared';
import { DropdownMenuPanel } from '@/shared/components/dropdowns/DropdownPanel';
import { useUserStore } from '@/stores';
import { backspaceKey, optionKey, shiftKey } from '@/utilities';

interface LayoutOptionsProps {
    compactTrigger?: boolean;
    compactTriggerType?: ButtonProps['type'];
    defaultOption?: string;
    disabled?: boolean;
    onOptionChange?: (option?: string) => void;
    option?: string;
    route: string;
    setOption?: Dispatch<SetStateAction<string>>;
    showKeyboardShortcutLabels?: boolean;
}

interface LayoutSortAction {
    disabled?: boolean;
    icon: ReactNode;
    id: number;
    key: string;
    title: string;
}

const LayoutOptions: FC<LayoutOptionsProps> = ({
    compactTrigger = false,
    compactTriggerType = 'dashed',
    defaultOption = 'updatedAt',
    disabled = false,
    onOptionChange,
    option,
    route,
    setOption,
    showKeyboardShortcutLabels = true,
}): JSX.Element => {
    const search = '';

    const query = useQuery();

    const currentSort = query.get('sort') as string;
    const selectedSortOption = onOptionChange || route === '/' ? option : currentSort || option;
    const showActiveSortBadge = Boolean(selectedSortOption && selectedSortOption !== defaultOption);

    const navigate = useNavigate();

    const handleSortOption: NonNullable<MenuProps['onClick']> = (e) => {
        if (onOptionChange) {
            onOptionChange(e?.key === 'clearSort' ? undefined : e?.key);
            return;
        }

        if (route !== '/') {
            navigate({
                search: `?sort=${e?.key}`,
            });
        } else {
            if (e.key === 'clearSort') {
                setOption?.('updatedAt');
                localStorage.setItem(`home-sortOption-${user?.id}`, 'updatedAt');
            } else {
                setOption?.(e?.key);
                localStorage.setItem(`home-sortOption-${user?.id}`, e.key as string);
            }
        }
    };

    const actions: LayoutSortAction[] = [
        {
            id: 8,
            key: 'alphabetical',
            title: 'Alphabetical',
            icon: <TbForms size='1rem' style={{ verticalAlign: '-0.2rem', marginRight: '0.5rem' }} />,
        },
        {
            id: 9,
            key: 'createdAt',
            title: 'Created At',
            icon: <TbClock size='1rem' style={{ verticalAlign: '-0.2rem', marginRight: '0.5rem' }} />,
        },
        {
            id: 0,
            key: 'updatedAt',
            title: 'Updated At',
            icon: <TbEdit size='1rem' style={{ verticalAlign: '-0.2rem', marginRight: '0.5rem' }} />,
        },
    ];

    const filteredActions = actions.filter((action) => {
        return action.title.toLowerCase().includes(search.toLowerCase());
    });

    const { selectedUser: user } = useUserStore();
    const { token } = useAntDesignTheme();

    return (
        <>
            <Dropdown
                disabled={disabled}
                trigger={['click']}
                placement='bottomRight'
                popupRender={() => (
                    <DropdownMenuPanel
                        key={currentSort}
                        selectedKeys={selectedSortOption ? [selectedSortOption] : []}
                        onClick={handleSortOption}
                        disabled={disabled}
                        style={{ marginTop: '0.5rem', minWidth: 300, maxWidth: 300 }}
                    >
                        <small style={{ marginLeft: '0.75rem', color: token.colorTextTertiary }}>Sort by..</small>
                        <Menu.Divider />
                        {filteredActions.length !== 0 ? (
                            filteredActions?.map((action) => {
                                return action.key === 'shared' ? (
                                    <Fragment>
                                        <Menu.Item key={action.key} disabled={action?.disabled}>
                                            <Row>
                                                <Col span={showKeyboardShortcutLabels ? 18 : 24}>
                                                    {action.icon}
                                                    {action.title}
                                                </Col>
                                                {showKeyboardShortcutLabels && (
                                                    <Col span={6} style={{ textAlign: 'right' }}>
                                                        <Tag
                                                            style={{
                                                                marginRight: 0,
                                                                paddingRight: 0,
                                                                color: token.colorTextTertiary,
                                                            }}
                                                            color='transparent'
                                                        >
                                                            {optionKey} {shiftKey} {action.id}
                                                        </Tag>
                                                    </Col>
                                                )}
                                            </Row>
                                        </Menu.Item>
                                        <Menu.Divider />
                                    </Fragment>
                                ) : (
                                    <Menu.Item key={action.key} disabled={action?.disabled}>
                                        <Row>
                                            <Col span={showKeyboardShortcutLabels ? 18 : 24}>
                                                {action.icon}
                                                {action.title}
                                            </Col>
                                            {showKeyboardShortcutLabels && (
                                                <Col span={6} style={{ textAlign: 'right' }}>
                                                    <Tag
                                                        style={{
                                                            marginRight: 0,
                                                            paddingRight: 0,
                                                            color: action?.disabled
                                                                ? token.colorTextQuaternary
                                                                : token.colorTextTertiary,
                                                        }}
                                                        color='transparent'
                                                    >
                                                        {optionKey} {shiftKey} {action.id}
                                                    </Tag>
                                                </Col>
                                            )}
                                        </Row>
                                    </Menu.Item>
                                );
                            })
                        ) : (
                            <EmptySection
                                height={16 * 8}
                                icon={<TbArrowsSort size='75%' style={{ color: token.colorTextTertiary }} />}
                                label='No Sort Options'
                                description='No sort options match your search.'
                            />
                        )}
                        <Menu.Divider />
                        <Menu.Item
                            key='clearSort'
                            icon={<TbBackspace size='1rem' />}
                            onClick={() => {
                                if (!onOptionChange) {
                                    navigate(route);
                                }
                            }}
                        >
                            <Row>
                                <Col span={showKeyboardShortcutLabels ? 16 : 24}>Clear Sort</Col>
                                {showKeyboardShortcutLabels && (
                                    <Col span={8} style={{ textAlign: 'right' }}>
                                        <Tag
                                            style={{
                                                marginRight: 0,
                                                paddingRight: 0,
                                                color: token.colorTextTertiary,
                                            }}
                                            color='transparent'
                                        >
                                            {optionKey} {shiftKey} {backspaceKey}
                                        </Tag>
                                    </Col>
                                )}
                            </Row>
                        </Menu.Item>
                    </DropdownMenuPanel>
                )}
            >
                {compactTrigger ? (
                    <Button disabled={disabled} size='small' type={compactTriggerType}>
                        <Badge
                            dot
                            color='blue'
                            offset={[-1, 1]}
                            style={{ display: showActiveSortBadge ? undefined : 'none' }}
                        >
                            <TbArrowsSort style={{ verticalAlign: '-0.175rem', color: token.colorTextTertiary }} />
                        </Badge>
                    </Button>
                ) : (
                    <span>
                        <Badge
                            dot
                            color='blue'
                            offset={[-11, 11]}
                            style={{ display: showActiveSortBadge ? undefined : 'none' }}
                        >
                            <Button disabled={disabled} type='text'>
                                <TbArrowsSort style={{ verticalAlign: '-0.175rem', color: token.colorTextTertiary }} />
                            </Button>
                        </Badge>
                    </span>
                )}
            </Dropdown>
        </>
    );
};

export default LayoutOptions;
